import React, { useState } from 'react';
import styled from 'styled-components';
import SubLayout from '../components/layout/SubLayout';
import { customerMenus } from '../data/customerMenus';

const Desc = styled.p`
  color: #555;
  font-size: 0.95rem;
  line-height: 1.8;
  margin-bottom: 40px;
`;

const Form = styled.form`
  max-width: 700px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;

  span { color: #e74c3c; margin-left: 2px; }
`;

const Input = styled.input`
  padding: 11px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: #0056b3; }
`;

const Select = styled.select`
  padding: 11px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  background: white;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus { border-color: #0056b3; }
`;

const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  height: 150px;
  resize: vertical;
  outline: none;
  font-family: inherit;
  line-height: 1.6;
  transition: border-color 0.2s;

  &:focus { border-color: #0056b3; }
`;

const SubmitButton = styled.button`
  padding: 14px;
  background: #0056b3;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  max-width: 200px;

  &:hover { background: #003d80; }
  &:disabled { background: #aaa; cursor: not-allowed; }
`;

const SuccessBox = styled.div`
  background: #f0faf4;
  border: 1px solid #27ae60;
  border-radius: 8px;
  padding: 30px;
  text-align: center;

  h3 { color: #27ae60; font-size: 1.3rem; margin-bottom: 10px; }
  p { color: #555; font-size: 0.95rem; }
`;

const ErrorBox = styled.div`
  background: #fdf2f2;
  border-left: 4px solid #c0392b;
  border-radius: 0 6px 6px 0;
  padding: 14px 18px;
  margin-bottom: 10px;
  font-size: 0.9rem;
  color: #a93226;
  line-height: 1.6;
`;

const InfoBox = styled.div`
  background: #f0f6ff;
  border-left: 4px solid #0056b3;
  border-radius: 0 8px 8px 0;
  padding: 18px 20px;
  margin-bottom: 30px;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.7;

  strong { color: #0056b3; }
`;

const RATE_LIMIT_MS = 60 * 60 * 1000; // 1시간에 1건
const LAST_SENT_KEY = 'doosung_last_inquiry_at';

// 상호(업체명)·성명 허용 문자. 입력 중에는 한글 자모(ㄱ, ㅏ 등)도 허용해야 IME 조합이 깨지지 않음
const NAME_INPUT_FILTER = /[^가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9\s()&.,·-]/g;
// 제출 시에는 완성된 한글만 허용
const NAME_PATTERN = /^[가-힣a-zA-Z0-9\s()&.,·-]{1,50}$/;
const NAME_HAS_LETTER = /[가-힣a-zA-Z]/;
const PHONE_PATTERN = /^(0\d{1,2}-\d{3,4}-\d{4}|1[5-9]\d{2}-\d{4})$/;

// 숫자만 남긴 뒤 번호 유형에 맞게 하이픈을 자동 삽입
const formatPhone = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (!d) return '';

  // 서울 지역번호 02
  if (d.startsWith('02')) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6, 10)}`;
  }

  // 대표번호 (1588, 1544, 1644 등)
  if (/^1[5-9]\d{2}/.test(d) && d.length <= 8) {
    if (d.length <= 4) return d;
    return `${d.slice(0, 4)}-${d.slice(4, 8)}`;
  }

  // 휴대전화 및 그 외 지역번호
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 11)}`;
};

// localStorage 사용 불가(시크릿 모드 등)여도 폼이 동작하도록 안전하게 접근
const readLastSentAt = () => {
  try {
    return Number(window.localStorage.getItem(LAST_SENT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeLastSentAt = (timestamp) => {
  try {
    window.localStorage.setItem(LAST_SENT_KEY, String(timestamp));
  } catch {
    /* 저장 실패는 무시 - 서버에서도 제한하므로 문제 없음 */
  }
};

const Inquiry = () => {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '', phone: '', email: '', type: '실내건축/인테리어', content: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      setForm(prev => ({ ...prev, phone: formatPhone(value) }));
      return;
    }
    if (name === 'name') {
      setForm(prev => ({ ...prev, name: value.replace(NAME_INPUT_FILTER, '').slice(0, 50) }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = form.name.trim();
    const phone = form.phone.trim();

    if (!name || !phone || !form.content.trim()) {
      setError('이름/업체명, 연락처, 문의 내용은 필수 입력 항목입니다.');
      return;
    }
    if (!NAME_PATTERN.test(name) || !NAME_HAS_LETTER.test(name)) {
      setError('이름/업체명은 한글·영문·숫자와 일부 기호( ) & . , · - 만 사용할 수 있습니다.');
      return;
    }
    if (!PHONE_PATTERN.test(phone)) {
      setError('연락처를 정확히 입력해 주세요. (예: 010-1234-5678)');
      return;
    }

    // 1시간에 1건 제한 (서버에서도 동일하게 검사)
    const elapsed = Date.now() - readLastSentAt();
    if (elapsed < RATE_LIMIT_MS) {
      const minutesLeft = Math.ceil((RATE_LIMIT_MS - elapsed) / 60000);
      setError(`문의는 1시간에 1건만 접수할 수 있습니다. 약 ${minutesLeft}분 후 다시 시도해 주세요.`);
      return;
    }

    setSending(true);
    setError('');

    try {
      const res = await fetch('/api/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '문의 전송에 실패했습니다.');
      }

      // 서버가 실제로 발송에 성공한 경우에만 완료 화면 표시
      writeLastSentAt(Date.now());
      setSubmitted(true);
    } catch (err) {
      setError(`${err.message} 문제가 계속되면 031-871-4424/5 로 연락 주세요.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <SubLayout
      title="견적문의"
      sidebarMenus={customerMenus}
      category="고객센터"
      categoryEn="Customer"
    >
      {submitted ? (
        <SuccessBox>
          <h3>✅ 문의가 접수되었습니다</h3>
          <p>담당자가 확인 후 빠른 시일 내에 연락드리겠습니다.<br />감사합니다.</p>
        </SuccessBox>
      ) : (
        <>
          <InfoBox>
            문의 내용을 작성해 주시면 담당자에게 즉시 접수됩니다.<br />
            전화 상담을 원하시면 <strong>031-871-4424/5</strong> 로 연락 주세요.
          </InfoBox>

          <Desc>
            상담 내용을 남겨주시면 담당자가 빠른 시일 내에 연락드리겠습니다.
            공사 예산, 면적, 시기 등 상세 내용을 적어주시면 더 정확한 상담이 가능합니다.
          </Desc>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Field>
                <Label>이름/업체명<span>*</span></Label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  maxLength={50}
                  placeholder="성함 또는 상호를 입력하세요"
                />
              </Field>
              <Field>
                <Label>연락처<span>*</span></Label>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="tel"
                  inputMode="numeric"
                  maxLength={13}
                  placeholder="010-0000-0000"
                />
              </Field>
            </Row>

            <Row>
              <Field>
                <Label>이메일</Label>
                <Input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                />
              </Field>
              <Field>
                <Label>문의 유형</Label>
                <Select name="type" value={form.type} onChange={handleChange}>
                  <option>실내건축/인테리어</option>
                  <option>조경식재/시설물</option>
                  <option>건물종합관리/방역</option>
                  <option>기타 문의</option>
                </Select>
              </Field>
            </Row>

            <Field>
              <Label>문의 내용<span>*</span></Label>
              <Textarea
                name="content"
                value={form.content}
                onChange={handleChange}
                placeholder="공사 예산, 면적, 시기, 요청 사항 등 상세 내용을 입력해 주세요."
              />
            </Field>

            {error && <ErrorBox>{error}</ErrorBox>}

            <SubmitButton type="submit" disabled={sending}>
              {sending ? '전송 중…' : '견적 문의 신청'}
            </SubmitButton>
          </Form>
        </>
      )}
    </SubLayout>
  );
};

export default Inquiry;
